'use server';

import { z } from 'zod';
import { headers } from 'next/headers';
import { prisma } from '@/lib/db/prisma';
import { getSession } from '@/lib/auth/session';
import { validateSubmittedAttempt } from '@/lib/scoring/validate';
import { isLeaderboardEligible } from '@/features/typing-engine/formulas';
import { rateLimit, getClientKey } from '@/lib/rate-limit';
import { evaluateAchievements } from '@/features/achievements/criteria';

const AttemptSchema = z.object({
  activityType: z.enum(['typing_test', 'lesson', 'practice', 'story', 'game']),
  activityRefSlug: z.string().nullable().optional(),
  durationSeconds: z.number().int().positive().max(3600),
  grossWpm: z.number().nonnegative(),
  netWpm: z.number().nonnegative(),
  accuracy: z.number().min(0).max(100),
  correctChars: z.number().int().nonnegative(),
  incorrectChars: z.number().int().nonnegative(),
  totalChars: z.number().int().nonnegative(),
  backspaces: z.number().int().nonnegative().default(0),
  metadata: z.record(z.unknown()).optional(),
});

export async function submitAttemptAction(rawAttempt) {
  const session = await getSession();
  if (!session) return { error: 'Not signed in.' };

  const headersList = await headers();
  const clientKey = getClientKey(headersList);
  const limited = rateLimit(`attempts:${session.user.id}:${clientKey}`, {
    max: 10,
    windowMs: 60_000,
  });
  if (!limited.allowed) return { error: 'Too many submissions. Try again in a minute.' };

  const parsed = AttemptSchema.safeParse(rawAttempt);
  if (!parsed.success) return { error: 'Invalid attempt data.' };

  const data = parsed.data;

  // Server-side recomputation for test submissions only.
  if (data.activityType === 'typing_test') {
    const validation = validateSubmittedAttempt({
      durationSeconds: data.durationSeconds,
      correctChars: data.correctChars,
      incorrectChars: data.incorrectChars,
      totalChars: data.totalChars,
      grossWpm: data.grossWpm,
      netWpm: data.netWpm,
      accuracy: data.accuracy,
    });
    if (!validation.valid) {
      return { error: `Attempt rejected: ${validation.reason}` };
    }
  }

  // Fetch history context for achievements (before inserting this attempt).
  const [priorTestCount, priorLessonCount, priorStoryCount, priorGameCount] = await Promise.all([
    prisma.activityAttempt.count({ where: { userId: session.user.id, activityType: 'typing_test' } }),
    prisma.lessonProgress.count({ where: { userId: session.user.id, completedAt: { not: null } } }),
    prisma.activityAttempt.count({ where: { userId: session.user.id, activityType: 'story' } }),
    prisma.activityAttempt.count({ where: { userId: session.user.id, activityType: 'game' } }),
  ]);

  const attempt = await prisma.activityAttempt.create({
    data: {
      userId: session.user.id,
      activityType: data.activityType,
      activityRefSlug: data.activityRefSlug || null,
      durationSeconds: data.durationSeconds,
      grossWpm: data.grossWpm,
      netWpm: data.netWpm,
      accuracy: data.accuracy,
      correctChars: data.correctChars,
      incorrectChars: data.incorrectChars,
      totalChars: data.totalChars,
      metadata: { backspaces: data.backspaces, ...(data.metadata || {}) },
    },
  });

  // Award achievements.
  const codes = evaluateAchievements(
    {
      netWpm: data.netWpm,
      accuracy: data.accuracy,
      activityType: data.activityType,
      activityRefSlug: data.activityRefSlug || '',
    },
    {
      totalTests: priorTestCount,
      totalLessonsCompleted: priorLessonCount,
      totalStories: priorStoryCount,
      totalGames: priorGameCount,
      priorBestWpm: 0,
    }
  );

  if (codes.length > 0) {
    const defs = await prisma.achievement.findMany({
      where: { code: { in: codes } },
      select: { id: true, code: true },
    });
    if (defs.length > 0) {
      await prisma.userAchievement.createMany({
        data: defs.map((d) => ({ userId: session.user.id, achievementId: d.id })),
        skipDuplicates: true,
      });
    }
  }

  const eligible = isLeaderboardEligible({
    durationSeconds: data.durationSeconds,
    netWpm: data.netWpm,
    accuracy: data.accuracy,
    correctChars: data.correctChars,
  });

  return {
    success: true,
    attemptId: attempt.id,
    leaderboardEligible: eligible,
    awardedAchievements: codes,
  };
}